import {
    Button,
    copyToClipboard,
    DataTable,
    DialogHandler,
    EmptyContent,
    EnhancedFormModel,
    fetchRoute,
    FormModel,
    FormsFactory,
    FullBar,
    GenericWrapperContext,
    Header,
    Icon,
    LoadingContainer,
    Search,
    SummaryEnhancedFormModel,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TOrder,
    TPromisePoolPartialList,
    TPromisePoolRef,
    TPromisePoolReturnData,
    UserModel,
    UsersFactory
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getBaseForm } from "../../../components/beeEditor/baseContent/baseForm"
import { showCreateResourceDialog } from "../../../components/dialogs/createResource"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { listsPromisePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { TFormsListing } from "../types"
import { buildDefaultUrls, buildFormScript, buildLink, getListingColsData, getListingRows } from "../utils"

export default function FormsListing({ listMainButton, listActions, extraTopActions, legacy = false }: TFormsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(200)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<string>(legacy ? "created_on" : "updated_on")
    const [forms, setForms] = useState<TGenericListReturn<SummaryEnhancedFormModel | FormModel>>()
    const [loading, setLoading] = useState(false)

    const [users, setUsers] = useState<{ [id: string]: UserModel }>()

    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})
    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    useEffect(() => {
        UsersFactory.list({
            page: 1,
            perPage: 1000
        }).then((res) => {
            const curUsers: any = {}
            res.data.forEach((user: UserModel) => {
                curUsers[user.id] = user
            })
            setUsers(curUsers)
        })
    }, [])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(() => {
        fetchForms()
    }, [page, perPage, order, orderBy, search])

    useEffect(() => {
        return stopPromisePools
    }, [])

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (e) {
            // pass
        }
    }

    function fetchForms() {
        setLoading(true)
        FormsFactory.list({
            page,
            perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            filter: search ? `name==${search}` : undefined,
            withCount: true
        }).then((res) => {

            const listIds = [...new Set(res.data.map(form => form.list_id))].map(id => ({ id }))
            listsPromisePool({ data: listIds, setter: setPartialLists, poolRef: partialListsPromiseRef })

            res.data = res.data.filter(form => {
                if (!form.name.includes("sys_hidden_")) {
                    if (legacy && form instanceof FormModel) return true
                    if (!legacy && form instanceof SummaryEnhancedFormModel) return true
                }
                return false
            })

            setForms(res)
            setLoading(false)
        }).catch(() => {
            setLoading(false)
        })
    }

    function handleCreateForm() {
        showCreateResourceDialog({
            titleKey: "general-create-form",
            addList: true
        }).then(({ btnRef, list, name }) => {
            btnRef.animate()
            const defaultUrls = buildDefaultUrls({ list: list, account: genericWrapper.account, user: genericWrapper.user, form: undefined })
            return FormsFactory.create({
                type: "enhanced",
                form: {
                    async_processing: true,
                    branding: "basic",
                    name,
                    list_id: list?.id,
                    post_redirect_url: defaultUrls.post_redirect_url,
                    double_opt_in_redirect_url: defaultUrls.double_opt_in_redirect_url,
                    content: {
                        json: getBaseForm()
                    }
                }
            })
        }).then((newForm) => {
            DialogHandler.hide()
            window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: newForm.id } })
        }).catch((e) => {
            if (e) {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            }
        })
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".formsSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number) {
        setPage(cPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleOpenForm(form: SummaryEnhancedFormModel | FormModel) {
        if (form instanceof SummaryEnhancedFormModel) {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: form.id } })
        } else {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formEditBasic, params: { formId: form.id } })
        }
    }

    function handleDuplicateForm(form: SummaryEnhancedFormModel | FormModel) {
        FormsFactory.get({ id: form.id })
            .then((data) => {
                data.name = data.name + ` (${t("general-copy")})`
                return FormsFactory.create({ form: data.toJson(), type: data instanceof EnhancedFormModel ? "enhanced" : "basic" })
            })
            .then(() => {
                fetchForms()
            }).catch((e) => {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            })
    }

    function handleRenameForm(form: SummaryEnhancedFormModel | FormModel) {
        showRenameDialog({ initName: form.name, resourceKey: "general-form_one" })
            .then(({ value }) => {
                form.name = value
                return form.save()
            }).then(() => {
                fetchForms()
                DialogHandler.hide()
            }).catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action"),
                    })
                }
            })
    }

    function handlePreviewForm(form: SummaryEnhancedFormModel | FormModel) {
        if (form instanceof SummaryEnhancedFormModel) {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: form.id } }) + "?preview=true"
        } else {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formEditBasic, params: { formId: form.id } })
        }

    }

    function handleCopyFormInfo(form: SummaryEnhancedFormModel | FormModel, type: "link" | "snippet") {
        if (form instanceof SummaryEnhancedFormModel) {
            if (type === "link") {
                copyToClipboard(buildLink({ form, account: genericWrapper.account, user: genericWrapper.user }))
            } else {
                copyToClipboard(buildFormScript({ form }))
            }
        } else {
            if (type === "link") {
                copyToClipboard(form.url.linked)
            } else {
                copyToClipboard(form.content.embedded)
            }

        }

    }

    function handleDeleteForm(form: SummaryEnhancedFormModel | FormModel) {
        showDeleteDialog({
            name: form.name,
            resourceKey: "general-form_one",
        }).then(({ btnRef }) => {
            btnRef.animate()
            return form.delete()
        }).then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("general-resource-deleted", { resource: t("general-form_one") })
            })
            fetchForms()
        }).catch((e) => {
            if (e)
                DialogHandler.show({
                    isExpress: true,
                    type: "warning",
                    title: t("general-error-action")
                })
        })
    }


    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData(legacy), [legacy])

    const rows: any[] = forms && forms.data.map((form: SummaryEnhancedFormModel | FormModel) => {
        const partialList: Partial<TPromisePoolPartialList> = form.list_id ? partialLists?.[form.list_id] : {}
        return getListingRows({
            form,
            partialList,
            users,
            handleOpenForm,
            handlePreviewForm,
            handleCopyFormInfo,
            handleRenameForm,
            handleDuplicateForm,
            handleDeleteForm,
            listMainButton,
            listActions
        })
    }) || []

    return <Stack className="formsListingContainer" gap={8}>
        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t(legacy ? "general-legacy-form_other" : "general-form_other")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
                headingTooltip={t("form-heading-tooltip")}
            />}
            right={[
                ...(!legacy ? [
                    <Button
                        key="create-form"
                        className="create-form-button"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateForm}
                        iconOnly
                        disabled={loading}
                    ><Icon name="Add18" /></Button>
                ] : []),
                <Button
                    className="search-form-button"
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>,
                ...(extraTopActions || [])
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("forms-listing-search-placeholder")} initValue={search} className={`formsSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <LoadingContainer loading={!forms}>
            {forms && forms.data.length === 0 && !search &&
                <EmptyContent
                    title={t("forms-listing-empty-title")}
                    desc={t("forms-listing-empty-desc")}
                    iconName="CreateForm44"
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateForm} iconOnly>
                        {t("general-create-form")}
                    </Button>}
                />
            }

            {forms && forms.data.length === 0 && search &&
                <EmptyContent
                    title={t("forms-listing-empty-search-title")}
                    desc={t("search-empty-desc")}
                />
            }

            {forms && forms.data.length > 0 &&
                <DataTable
                    initState={{
                        order,
                        orderBy
                    }}
                    showPagination={false}
                    className={`formsDataTable ${loading ? "loading" : ""}`}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                />
            }

        </LoadingContainer>
    </Stack>
}